import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

export class RobustErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      retryCount: 0
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('RobustErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleAutoRetry = () => {
    if (this.state.retryCount < 3) {
      this.retryTimeoutId = setTimeout(() => {
        this.handleRetry();
      }, 2000);
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error?.message?.includes('Loading chunk') ||
                          this.state.error?.message?.includes('ChunkLoadError') ||
                          this.state.error?.message?.includes('Failed to fetch');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="space-y-4">
              <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
              <div className="space-y-2">
                <h1 className="text-xl font-semibold text-foreground">
                  {isChunkError ? 'Loading Error' : 'Something went wrong'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isChunkError 
                    ? 'Failed to load application resources. This might be due to a network issue or app update.'
                    : 'An unexpected error occurred. Please try again.'
                  }
                </p>
                {this.state.error?.message && (
                  <details className="mt-4">
                    <summary className="text-xs text-muted-foreground cursor-pointer">
                      Error details
                    </summary>
                    <p className="mt-2 text-xs text-muted-foreground font-mono bg-muted p-2 rounded text-left">
                      {this.state.error.message}
                    </p>
                  </details>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={this.handleRetry}
                className="w-full"
                disabled={this.state.retryCount >= 3}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again {this.state.retryCount > 0 && `(${this.state.retryCount}/3)`}
              </Button>
              
              {isChunkError && (
                <Button 
                  onClick={() => {
                    // Use navigation helper for safer reload
                    import('@/hooks/useNavigationHelper').then(async ({ useNavigationHelper }) => {
                      const { safeReload } = useNavigationHelper();
                      await safeReload('robust-error-boundary-chunk-reload');
                    }).catch(() => {
                      console.warn('⚠️ RobustErrorBoundary fallback - attempting navigation recovery');
                      try {
                        window.history.replaceState(null, '', window.location.pathname);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      } catch (navError) {
                        console.warn('Navigation recovery failed; user may refresh manually', navError);
                      }
                    });
                  }}
                  variant="secondary"
                  className="w-full"
                >
                  Reload Page
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { useNavigationHelper } = await import('@/hooks/useNavigationHelper');
                    const { navigate } = useNavigationHelper();
                    navigate('/', 'robust-error-boundary-home');
                  } catch (error) {
                    console.warn('⚠️ RobustErrorBoundary fallback navigation:', error);
                    window.location.href = '/';
                  }
                }}
                className="w-full"
              >
                Go to Home
              </Button>
              
              {this.state.retryCount < 3 && (
                <p className="text-xs text-muted-foreground">
                  Auto-retry in 2 seconds...
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}